/**
 * Fix recommendations — per-end-use potential savings and the control that
 * governs them.
 *
 * The $, kWh, share and operating window are measured, straight from the
 * payload. The "what to check" line is a static per-end-use mechanism: the
 * control an operator would inspect for that end-use, not a computed
 * instruction. It stays qualitative on purpose -- unlike C1's setpoint model
 * (which has a kW/degF slope behind it) there is no fan-curve, VFD-speed or
 * static-pressure model here, so anything QUANTITATIVE about how far to trim
 * would be invented. Keep these lines naming a control to look at; do not let
 * them grow numbers until something derives them.
 */
import type React from "react"

import { useFormatCurrency } from "@/formatters/use-format-currency"

import { Box, Stack, Typography } from "@mui/material"

import { Card } from "../../homepage/atoms"
import { enduseColor, enduseLabel } from "../variance-route/enduse-colors"
import type { E2Driver, SiteMetaSchedule } from "./schedule-misalignment.types"

/** Illustrative control to inspect per end-use — NOT computed. */
const MECHANISM: { match: RegExp; check: string }[] = [
  {
    match: /fan/i,
    check:
      "Optimal-start / warm-up override, and the unoccupied fan mode on the AHU schedule.",
  },
  {
    match: /lighting/i,
    check:
      "Sweep and override timers, plus any after-hours cleaning schedule holding zones on.",
  },
  {
    match: /pump/i,
    check:
      "Loop enable conditions — pumps often follow a call that never drops out overnight.",
  },
  {
    match: /heat_(rejection|recovery)/i,
    check: "Enable/disable interlock with the primary equipment it serves.",
  },
  {
    match: /plug/i,
    check: "Controlled receptacles and after-hours load policy.",
  },
]

const mechanismFor = (enduse: string): string =>
  MECHANISM.find((m) => m.match.test(enduse))?.check ??
  "Confirm this end-use is released to unoccupied outside operating hours."

const fmtHour = (h: number): string => {
  const hr = Math.floor(h)
  const min = Math.round((h - hr) * 60)
  const ampm = hr < 12 || hr === 24 ? "AM" : "PM"
  const h12 = hr % 12 || 12
  return min
    ? `${h12}:${String(min).padStart(2, "0")} ${ampm}`
    : `${h12} ${ampm}`
}

const windowText = (s?: SiteMetaSchedule): string | null => {
  if (!s) {
    return null
  }
  if (s.always_on) {
    return "always on (24/7)"
  }
  return s.weekday
    ? `${fmtHour(s.weekday[0])} – ${fmtHour(s.weekday[1])} weekdays`
    : null
}

export const FixRecommendations: React.FC<{
  drivers: E2Driver[]
  schedule?: SiteMetaSchedule
}> = ({ drivers, schedule }) => {
  const { formatCurrency } = useFormatCurrency({
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
  const top = drivers.slice(0, 3)
  if (top.length === 0) {
    return null
  }
  const win = windowText(schedule)

  return (
    <Card sx={{ p: 2.5 }}>
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: "center", flexWrap: "wrap", mb: 1.5 }}
      >
        <Typography
          component="h3"
          sx={{ fontSize: "1.125rem", fontWeight: "bold" }}
          variant="body1"
        >
          Recommended fixes
        </Typography>
      </Stack>
      <Typography color="text.secondary" sx={{ mb: 2 }} variant="body2">
        Where the potential savings sit, and the control that governs them.
      </Typography>

      <Stack spacing={1.5}>
        {top.map((d) => (
          <Box
            key={d.enduse}
            sx={{
              borderLeft: 3,
              borderColor: enduseColor(d.enduse),
              pl: 1.75,
              py: 0.5,
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              sx={{ alignItems: "baseline", flexWrap: "wrap" }}
            >
              <Typography sx={{ fontWeight: "bold" }} variant="body1">
                {enduseLabel(d.enduse)}
              </Typography>
              <Typography sx={{ fontWeight: "bold" }} variant="body2">
                {formatCurrency(d.impact_dollars)}
              </Typography>
              <Typography color="text.secondary" variant="body2">
                {d.share_pct.toFixed(0)}% of potential savings ·{" "}
                {Math.round(d.wasted_kwh).toLocaleString()} kWh
              </Typography>
            </Stack>
            {win ? (
              <Typography
                color="text.secondary"
                component="p"
                sx={{ mt: 0.25 }}
                variant="body2"
              >
                Running outside {win}.
              </Typography>
            ) : null}
            <Typography component="p" sx={{ mt: 0.5 }} variant="body2">
              <Box component="span" sx={{ fontWeight: 700 }}>
                What to check:{" "}
              </Box>
              {mechanismFor(d.enduse)}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Card>
  )
}
